import { useEffect, useMemo, useState } from "react";
import { Heart, LogOut, MapPin, Calendar, Clock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LeafletMap } from "@/components/LeafletMap";
import { Button } from "@/components/ui/button";

export default function RadarScreen() {
  const { user, logout } = useAuth();
  const [matches, setMatches] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [scanning, setScanning] = useState(true);

  useEffect(() => {
    api.get("/my-matches").then(({ data }) => setMatches(data)).catch(() => {});
    api.get("/nearby-members").then(({ data }) => setNearby(data)).catch(() => {});
    const t = setTimeout(() => setScanning(false), 4200);
    return () => clearTimeout(t);
  }, []);

  const lat = user?.latitude ?? 40.7128;
  const lng = user?.longitude ?? -74.006;
  const place = user?.location_name || `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
  const nearbyMarkers = useMemo(() => nearby.map((m) => ({ lat: m.latitude, lng: m.longitude, label: m.label })), [nearby]);

  return (
    <div className="relative min-h-screen" data-testid="radar-page">
      <div className="absolute inset-0 z-0">
        <LeafletMap lat={lat} lng={lng} markers={nearbyMarkers} interactive={true} zoom={13} height="100vh" drift />
      </div>
      <div className="absolute inset-0 z-[400] bg-gradient-to-b from-amber-100/20 via-transparent to-rose-100/20 pointer-events-none" />

      {/* Radar overlay */}
      <div className="absolute inset-0 z-[401] flex items-center justify-center pointer-events-none">
        <div className="relative w-[320px] h-[320px] flex items-center justify-center">
          <div className="absolute inset-0 rounded-full border border-[#F43F5E]/40" />
          <div className="absolute inset-8 rounded-full border border-[#F43F5E]/30" />
          <div className="absolute inset-16 rounded-full border border-[#F43F5E]/20" />
          {scanning && <div className="absolute inset-0 radar-sweep" />}
          <div className="absolute w-24 h-24 rounded-full border-2 border-[#F43F5E]/60 radar-ping" />
        </div>
      </div>

      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 z-[500] flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2 bg-white/80 backdrop-blur rounded-full px-4 py-2 shadow-sm border border-amber-100">
          <Heart className="text-[#F43F5E] fill-[#F43F5E]" size={20} />
          <span className="font-display font-bold text-stone-900">Love at geo-point</span>
        </div>
        <Button data-testid="logout-btn" onClick={logout} variant="outline" size="sm" className="bg-white/90 backdrop-blur border-stone-200 gap-2 text-stone-700">
          <LogOut size={15} /> Sign out
        </Button>
      </header>

      {/* Message card */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[500] w-[92%] max-w-lg fade-up">
        <div className="bg-white/90 backdrop-blur-xl border border-amber-100 rounded-2xl p-6 shadow-xl" data-testid="radar-message">
          <div className="flex items-center gap-2 text-[#F43F5E] text-sm font-semibold uppercase tracking-widest">
            <Sparkles size={15} /> {scanning ? "Romance Radar · scanning…" : "Signal locked"}
          </div>
          <p className="text-xl font-display font-bold mt-2 leading-snug text-stone-900">
            We believe you'll find love near <span className="text-[#F43F5E]">{place}</span>.
          </p>
          {!scanning && nearby.length > 0 && (
            <p className="text-sm text-stone-600 mt-2" data-testid="nearby-count">
              <span className="font-semibold text-[#F43F5E]">{nearby.length}</span> {nearby.length === 1 ? "member is" : "members are"} in your area — see the pins on the map.
            </p>
          )}

          {matches.length > 0 && (
            <div className="mt-5 space-y-3" data-testid="match-events-list">
              <div className="text-xs uppercase tracking-widest text-stone-400">Your invitations</div>
              {matches.map((m) => (
                <div key={m.id} className="bg-amber-50 border border-amber-100 rounded-xl p-4" data-testid="match-event-card">
                  <div className="font-semibold text-stone-900">{m.title}</div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-600 mt-2">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {m.date}</span>
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {m.time}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={14} /> {m.location_name}</span>
                  </div>
                  {m.note && <p className="text-sm text-stone-500 mt-2">{m.note}</p>}
                </div>
              ))}
            </div>
          )}
          {!scanning && matches.length === 0 && (
            <p className="text-sm text-stone-500 mt-3">Sit tight — our matchmaker will place you at the perfect spot soon.</p>
          )}
        </div>
      </div>
    </div>
  );
}
